// GC Labs — RAG QA bench.
//
// The internal tool Olivier uses to test what the Coach will say,
// inspect what was retrieved, and detect fabricated citations. Runs
// against the backend built in Labs MR 1 + MR 2:
//   POST /admin/labs/query · POST /admin/labs/enrich-preview ·
//   GET /admin/labs/test-runs · GET /admin/labs/test-runs/{id} ·
//   DELETE /admin/labs/test-runs/{id}
//
// Layout: 3 columns at ≥1280 (query · response · retrieval), stacks
// vertically below. History table below the grid. Compare mode
// collapses the right column to a 46px strip so both responses stay
// legible.
//
// Route: /admin/labs (top-level nav item, no INSIGHTS grouping).
// RBAC: kb:read + kb:write on super_admin + content_editor only.

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from '@gc-coach/design-system';
import type {
  Domain,
  KBEntryType,
  LabsCoachResponse,
  LabsRetrievedChunk,
  LabsTestRun,
  LabsTestRunSummary,
  PersonaSummary,
} from '@gc-coach/shared';
import {
  deleteLabsTestRun,
  getLabsTestRun,
  listLabsTestRuns,
  listPersonae,
  previewLabsEnrichment,
  runLabsQuery,
} from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import { AdminShell, PageHeader } from '../shell/index.js';
import styles from './LabsPage.module.css';

// ─── Types local to the page ────────────────────────────────────────────

const DOMAINS: Domain[] = [
  'D1_strategy_org',
  'D2_external_counsel',
  'D3_legal_ops_tech',
  'D4_team_dev',
  'D5_kpis_performance',
  'D6_budget_finance',
  'D7_stakeholder_comms',
  'D8_governance_risk',
];

const DOMAIN_LABELS: Record<Domain, string> = {
  D1_strategy_org: 'D1 · Strategy & Organization',
  D2_external_counsel: 'D2 · External Counsel',
  D3_legal_ops_tech: 'D3 · Legal Ops & Tech',
  D4_team_dev: 'D4 · Team Development',
  D5_kpis_performance: 'D5 · KPIs & Performance',
  D6_budget_finance: 'D6 · Budget & Finance',
  D7_stakeholder_comms: 'D7 · Stakeholder Comms',
  D8_governance_risk: 'D8 · Governance & Risk',
};

type LangValue = 'all' | 'en' | 'fr';

// ─── Main component ────────────────────────────────────────────────────

export function LabsPage() {
  const { t, tt } = useI18n();

  // Query panel state
  const [personas, setPersonas] = useState<PersonaSummary[]>([]);
  const [personaId, setPersonaId] = useState<string | null>(null);
  const [domainFilter, setDomainFilter] = useState<Domain | null>(null);
  const [languageFilter, setLanguageFilter] = useState<LangValue>('all');
  const [queryText, setQueryText] = useState('');
  const [enrichEnabled, setEnrichEnabled] = useState(false);
  const [enrichedQuery, setEnrichedQuery] = useState<string | null>(null);
  const [enrichPreviewing, setEnrichPreviewing] = useState(false);
  // Decomposition — compound-question support (Olivier 2026-10-31). When
  // on, Haiku splits the raw query into 2-3 sub-queries before retrieval;
  // retrieval unions results across sub-queries. See coach-engine
  // CoachRetrievalOptions.subQueries.
  const [decomposeEnabled, setDecomposeEnabled] = useState(false);
  const [compareAblation, setCompareAblation] = useState(false);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  // Score threshold — chunks below this are dropped from the prompt (backend
  // caps at 15 via topK as a safety net). Olivier's ask 2026-08-25: default
  // 0.70 so weak matches don't pad the response and dilute the answer. Kept
  // as a number in [0..1]; UI shows as 0.70 with a small input.
  const [minScore, setMinScore] = useState(0.7);

  // Run state
  const [running, setRunning] = useState(false);
  const [runElapsedSecs, setRunElapsedSecs] = useState(0);
  const [runError, setRunError] = useState<string | null>(null);
  const [currentRun, setCurrentRun] = useState<LabsTestRun | null>(null);

  // Interaction state
  const [expandedRowIdx, setExpandedRowIdx] = useState<number | null>(null);
  const [focusedCitation, setFocusedCitation] = useState<number | null>(null);
  const [retrievalCollapsed, setRetrievalCollapsed] = useState(false);

  // History state
  const [recentRuns, setRecentRuns] = useState<LabsTestRunSummary[]>([]);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Refs for scroll-to-chunk
  const retrievalRowRefs = useRef<Array<HTMLDivElement | null>>([]);

  // ─── Bootstrap: load personas + recent tests ─────────────────────
  useEffect(() => {
    void (async () => {
      const res = await listPersonae({ status: 'active' });
      if (res.ok) setPersonas([...res.data.personae]);
    })();
  }, []);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    const res = await listLabsTestRuns(40);
    setHistoryLoading(false);
    if (res.ok) setRecentRuns(res.data.runs);
  }, []);

  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);

  // ─── Elapsed timer for the running state ─────────────────────────
  useEffect(() => {
    if (!running) return;
    setRunElapsedSecs(0);
    const t0 = Date.now();
    const id = window.setInterval(() => {
      setRunElapsedSecs(Math.floor((Date.now() - t0) / 1000));
    }, 500);
    return () => window.clearInterval(id);
  }, [running]);

  // Reset enrichment preview when the toggle changes or query changes
  useEffect(() => {
    setEnrichedQuery(null);
  }, [enrichEnabled, queryText]);

  // Reset the compare-mode retrieval-collapse when compare goes off
  useEffect(() => {
    if (!compareAblation) setRetrievalCollapsed(false);
  }, [compareAblation]);

  // ─── Actions ─────────────────────────────────────────────────────
  const canRun = queryText.trim().length > 0 && !running;

  const onRunQuery = useCallback(async () => {
    if (!canRun) return;
    setRunning(true);
    setRunError(null);
    setExpandedRowIdx(null);
    setFocusedCitation(null);
    setRetrievalCollapsed(false);
    const res = await runLabsQuery({
      raw_query: queryText.trim(),
      persona_id: personaId,
      domain_filter: domainFilter,
      language_filter: languageFilter === 'all' ? null : languageFilter,
      include_drafts: includeDrafts,
      enrich_query: enrichEnabled,
      decompose_query: decomposeEnabled,
      compare_ablation: compareAblation,
      min_score: minScore,
    });
    setRunning(false);
    if (res.ok) {
      setCurrentRun(res.data.test_run);
      // Auto-collapse retrieval when compare is on (the ablation view is
      // full-attention; retrieval detail can wait a click)
      if (res.data.test_run.input.compare_ablation) {
        setRetrievalCollapsed(true);
      }
      void refreshHistory();
    } else {
      setRunError(res.message ?? t('labs.errorGeneric'));
    }
  }, [
    canRun,
    queryText,
    personaId,
    domainFilter,
    languageFilter,
    includeDrafts,
    enrichEnabled,
    decomposeEnabled,
    compareAblation,
    minScore,
    refreshHistory,
    t,
  ]);

  const onPreviewEnrichment = useCallback(async () => {
    if (!enrichEnabled) return;
    const raw = queryText.trim();
    if (raw.length === 0) return;
    setEnrichPreviewing(true);
    const res = await previewLabsEnrichment({
      raw_query: raw,
      language: languageFilter === 'fr' ? 'fr' : 'en',
    });
    setEnrichPreviewing(false);
    if (res.ok) setEnrichedQuery(res.data.enriched_query);
  }, [enrichEnabled, queryText, languageFilter]);

  const onLoadHistoryRow = useCallback(async (id: string) => {
    // Load full run detail then pre-fill the query panel (no auto-run —
    // reviewer clicks Run themselves)
    const res = await getLabsTestRun(id);
    if (!res.ok) return;
    const run = res.data.test_run;
    setQueryText(run.input.raw_query);
    setPersonaId(run.input.persona_id);
    setDomainFilter(run.input.domain_filter);
    setLanguageFilter(run.input.language_filter ?? 'all');
    setIncludeDrafts(run.input.include_drafts);
    setEnrichEnabled(run.input.enrich_query);
    setDecomposeEnabled(run.input.decompose_query === true);
    setCompareAblation(run.input.compare_ablation);
    // Restore the threshold from the loaded run. Absent (pre-feature runs)
    // → fall back to today's default 0.70 so a reviewer re-running an old
    // query gets the current sane threshold, not the old top-15-only behaviour.
    setMinScore(typeof run.input.min_score === 'number' ? run.input.min_score : 0.7);
    setCurrentRun(run);
    setExpandedRowIdx(null);
    setFocusedCitation(null);
    setRetrievalCollapsed(run.input.compare_ablation);
  }, []);

  const onConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const res = await deleteLabsTestRun(pendingDelete);
    setPendingDelete(null);
    if (res.ok) void refreshHistory();
  }, [pendingDelete, refreshHistory]);

  // ─── Textarea keyboard: Enter = run, Shift+Enter = newline ────────
  const onQueryKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void onRunQuery();
      }
    },
    [onRunQuery],
  );

  // Citation → retrieval row bridge
  const onCitationClick = useCallback((n: number, orphan: boolean) => {
    if (orphan) return; // no target row for orphans
    setFocusedCitation(n);
    setExpandedRowIdx(n - 1);
    // If compare mode has retrieval collapsed, restore it first
    setRetrievalCollapsed(false);
    // Scroll to the row on next paint
    window.setTimeout(() => {
      const el = retrievalRowRefs.current[n - 1];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 30);
  }, []);

  // ─── Render ──────────────────────────────────────────────────────
  const retrievedChunks = currentRun?.retrieved_chunks ?? [];
  const totalCited = retrievedChunks.filter((c) => c.was_cited_by_response).length;
  // Coaching-scope decline (guardrail): coach engine set scope_declined=true
  // via the return_coach_reply tool arg (see coach-engine COACH_REPLY_SCHEMA).
  // When true, retrieval fired but the coach didn't use it to answer — so
  // showing the retrieval panel would misrepresent what happened. We swap
  // the panel for a compact "not used" strip that explains why.
  const scopeDeclined = currentRun?.response_with_corpus.scope_declined === true;

  return (
    <AdminShell
      activeKey="labs"
      breadcrumb={[t('labs.breadcrumbLabs')]}
      state={{ kind: 'normal' }}
    >
      <PageHeader title={t('labs.title')} sub={t('labs.subtitle')} />

      {runError && (
        <Alert severity="danger" onDismiss={() => setRunError(null)}>
          {runError}
        </Alert>
      )}

      <div
        className={
          // Apply the compact-46px right column ONLY when the reviewer has
          // collapsed retrieval. If they expand it (or if compare is off),
          // give retrieval its normal minmax(280px, 34%) width. Previously
          // this forced 46px whenever compare was on, so an expanded
          // retrieval panel got squished into the 46px slot.
          compareAblation && currentRun && retrievalCollapsed
            ? `${styles.grid} ${styles.gridCompare}`
            : styles.grid
        }
      >
        {/* LEFT — query controls */}
        <QueryPanel
          t={t}
          personas={personas}
          personaId={personaId}
          onPersonaChange={setPersonaId}
          domainFilter={domainFilter}
          onDomainChange={setDomainFilter}
          languageFilter={languageFilter}
          onLanguageChange={setLanguageFilter}
          queryText={queryText}
          onQueryTextChange={setQueryText}
          onQueryKeyDown={onQueryKeyDown}
          enrichEnabled={enrichEnabled}
          onEnrichToggle={setEnrichEnabled}
          enrichedQuery={enrichedQuery}
          enrichPreviewing={enrichPreviewing}
          onPreviewEnrichment={onPreviewEnrichment}
          decomposeEnabled={decomposeEnabled}
          onDecomposeToggle={setDecomposeEnabled}
          subQueries={currentRun?.sub_queries ?? null}
          compareAblation={compareAblation}
          onCompareToggle={setCompareAblation}
          includeDrafts={includeDrafts}
          onIncludeDraftsToggle={setIncludeDrafts}
          minScore={minScore}
          onMinScoreChange={setMinScore}
          canRun={canRun}
          running={running}
          runElapsedSecs={runElapsedSecs}
          onRun={onRunQuery}
        />

        {/* MIDDLE — response */}
        <ResponsePanel
          t={t}
          tt={tt}
          run={currentRun}
          running={running}
          runElapsedSecs={runElapsedSecs}
          runError={runError}
          onRetry={onRunQuery}
          focusedCitation={focusedCitation}
          onCitationClick={onCitationClick}
          onCitationFocus={setFocusedCitation}
          enrichEnabled={enrichEnabled}
        />

        {/* RIGHT — retrieval (or scope-decline strip when the coach declined) */}
        {scopeDeclined ? (
          <ScopeDeclineRetrievalStrip t={t} retrievedCount={retrievedChunks.length} />
        ) : compareAblation && currentRun && retrievalCollapsed ? (
          <button
            type="button"
            className={styles.retrievalCollapsed}
            onClick={() => setRetrievalCollapsed(false)}
            aria-label={tt('labs.compareRetrievalCollapsedTemplate', { n: retrievedChunks.length })}
            aria-expanded="false"
          >
            {tt('labs.compareRetrievalCollapsedTemplate', { n: retrievedChunks.length })}
          </button>
        ) : (
          <RetrievalPanel
            t={t}
            tt={tt}
            chunks={retrievedChunks}
            totalCited={totalCited}
            running={running}
            expandedRowIdx={expandedRowIdx}
            onRowClick={(i) => setExpandedRowIdx((prev) => (prev === i ? null : i))}
            onRowFocus={(i) => setFocusedCitation(i + 1)}
            focusedCitation={focusedCitation}
            rowRefs={retrievalRowRefs}
            hasRunOnce={currentRun !== null}
            onClearFilters={() => {
              setDomainFilter(null);
              setLanguageFilter('all');
            }}
            onIncludeDrafts={() => setIncludeDrafts(true)}
          />
        )}
      </div>

      {/* HISTORY table (full-width below) */}
      <RecentTests
        t={t}
        tt={tt}
        runs={recentRuns}
        loading={historyLoading}
        pendingDelete={pendingDelete}
        onLoad={onLoadHistoryRow}
        onDeleteRequest={setPendingDelete}
        onDeleteConfirm={onConfirmDelete}
        onDeleteCancel={() => setPendingDelete(null)}
      />
    </AdminShell>
  );
}

// ─── QueryPanel (left column) ──────────────────────────────────────────

interface QueryPanelProps {
  t: (k: string) => string;
  personas: PersonaSummary[];
  personaId: string | null;
  onPersonaChange: (id: string | null) => void;
  domainFilter: Domain | null;
  onDomainChange: (d: Domain | null) => void;
  languageFilter: LangValue;
  onLanguageChange: (l: LangValue) => void;
  queryText: string;
  onQueryTextChange: (s: string) => void;
  onQueryKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  enrichEnabled: boolean;
  onEnrichToggle: (b: boolean) => void;
  enrichedQuery: string | null;
  enrichPreviewing: boolean;
  onPreviewEnrichment: () => void;
  decomposeEnabled: boolean;
  onDecomposeToggle: (b: boolean) => void;
  /** Populated after a run when decomposition actually split the query
   *  into >1 sub-queries. Rendered as a compact list under the retrieval
   *  query field. */
  subQueries: string[] | null;
  compareAblation: boolean;
  onCompareToggle: (b: boolean) => void;
  includeDrafts: boolean;
  onIncludeDraftsToggle: (b: boolean) => void;
  minScore: number;
  onMinScoreChange: (n: number) => void;
  canRun: boolean;
  running: boolean;
  runElapsedSecs: number;
  onRun: () => void;
}

function QueryPanel(p: QueryPanelProps) {
  const { t } = p;
  const hasPersonas = p.personas.length > 0;
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>{t('labs.queryPanelHeader')}</div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('labs.personaLabel')}</label>
        <select
          className={styles.select}
          value={p.personaId ?? ''}
          onChange={(e) => p.onPersonaChange(e.target.value || null)}
          disabled={!hasPersonas}
        >
          <option value="">
            {hasPersonas ? t('labs.personaDefault') : t('labs.noPersonasYet')}
          </option>
          {p.personas.map((pers) => (
            <option key={pers.id} value={pers.id}>
              {pers.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('labs.domainLabel')}</label>
        <select
          className={styles.select}
          value={p.domainFilter ?? ''}
          onChange={(e) => p.onDomainChange((e.target.value as Domain) || null)}
        >
          <option value="">{t('labs.domainAll')}</option>
          {DOMAINS.map((d) => (
            <option key={d} value={d}>
              {DOMAIN_LABELS[d]}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('labs.languageLabel')}</label>
        <div className={styles.chipRow}>
          {(['all', 'en', 'fr'] as LangValue[]).map((v) => (
            <button
              key={v}
              type="button"
              className={`${styles.chip} ${p.languageFilter === v ? styles.chipActive : ''}`}
              onClick={() => p.onLanguageChange(v)}
            >
              {v === 'all' ? t('labs.languageAll') : v === 'en' ? t('labs.languageEn') : t('labs.languageFr')}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('labs.queryTextareaLabel')}</label>
        <textarea
          className={styles.textarea}
          value={p.queryText}
          onChange={(e) => p.onQueryTextChange(e.target.value)}
          onKeyDown={p.onQueryKeyDown}
          rows={3}
        />
        <div className={styles.fieldHint}>{t('labs.queryTextareaHint')}</div>
      </div>

      <div className={styles.field}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={p.enrichEnabled}
            onChange={(e) => p.onEnrichToggle(e.target.checked)}
          />
          <span>{t('labs.enrichCheckbox')}</span>
        </label>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel}>{t('labs.retrievalQueryLabel')}</label>
        <RetrievalQueryField
          t={t}
          rawQuery={p.queryText}
          enrichEnabled={p.enrichEnabled}
          enrichedQuery={p.enrichedQuery}
        />
        {p.enrichEnabled && (
          <div className={styles.previewButtonRow}>
            <button
              type="button"
              className={styles.previewButton}
              onClick={p.onPreviewEnrichment}
              disabled={p.enrichPreviewing || p.queryText.trim().length === 0}
            >
              {p.enrichPreviewing
                ? t('labs.previewingButton')
                : p.enrichedQuery
                  ? t('labs.rePreviewEnrichmentButton')
                  : t('labs.previewEnrichmentButton')}
            </button>
          </div>
        )}
        <div className={styles.fieldHint}>
          {p.enrichEnabled ? t('labs.retrievalQueryEnrichedHint') : t('labs.retrievalQueryRawHint')}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={p.decomposeEnabled}
            onChange={(e) => p.onDecomposeToggle(e.target.checked)}
          />
          <span>{t('labs.decomposeCheckbox')}</span>
        </label>
        <div className={styles.fieldHint}>{t('labs.decomposeHint')}</div>
        {p.decomposeEnabled && p.subQueries && p.subQueries.length > 0 && (
          <div className={styles.subQueriesList}>
            <div className={styles.subQueriesHeading}>{t('labs.subQueriesHeading')}</div>
            <ol>
              {p.subQueries.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={p.compareAblation}
            onChange={(e) => p.onCompareToggle(e.target.checked)}
          />
          <span>{t('labs.compareCheckbox')}</span>
        </label>
      </div>

      <div className={styles.field}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={p.includeDrafts}
            onChange={(e) => p.onIncludeDraftsToggle(e.target.checked)}
          />
          <span>{t('labs.draftsCheckbox')}</span>
        </label>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="labs-min-score">
          {t('labs.minScoreLabel')}
        </label>
        <input
          id="labs-min-score"
          type="number"
          min={0}
          max={1}
          step={0.05}
          value={p.minScore}
          onChange={(e) => {
            const raw = parseFloat(e.target.value);
            if (!Number.isFinite(raw)) return;
            const clamped = Math.max(0, Math.min(1, raw));
            p.onMinScoreChange(Number(clamped.toFixed(2)));
          }}
          className={styles.textInput}
        />
        <span className={styles.fieldHint}>{t('labs.minScoreHint')}</span>
      </div>

      <div className={styles.runButtonRow}>
        <Button kind="primary" onClick={p.onRun} disabled={!p.canRun}>
          {p.running ? <Spinner /> : t('labs.runButton')}
        </Button>
        {p.running && (
          <span className={styles.runningNote}>
            {t('labs.runningButton')} · {p.runElapsedSecs}s
          </span>
        )}
      </div>
    </div>
  );
}

function RetrievalQueryField({
  t,
  rawQuery,
  enrichEnabled,
  enrichedQuery,
}: {
  t: (k: string) => string;
  rawQuery: string;
  enrichEnabled: boolean;
  enrichedQuery: string | null;
}) {
  const trimmed = rawQuery.trim();
  if (enrichEnabled) {
    if (enrichedQuery != null && enrichedQuery.length > 0) {
      return (
        <div className={styles.retrievalQueryField}>
          <span className={styles.retrievalQueryEnriched}>{enrichedQuery}</span>
        </div>
      );
    }
    // enrich on, not yet previewed
    return (
      <div className={styles.retrievalQueryField}>
        <span className={styles.retrievalQueryNotPreviewed}>
          {t('labs.retrievalQueryNotPreviewed')}
        </span>
      </div>
    );
  }
  // enrich off — show raw or empty
  return (
    <div className={styles.retrievalQueryField}>
      {trimmed.length > 0 ? trimmed : (
        <span className={styles.retrievalQueryNotPreviewed}>
          {t('labs.retrievalQueryEmpty')}
        </span>
      )}
    </div>
  );
}

// ─── ResponsePanel (middle column) ─────────────────────────────────────

interface ResponsePanelProps {
  t: (k: string) => string;
  tt: (k: string, values: Record<string, string | number>) => string;
  run: LabsTestRun | null;
  running: boolean;
  runElapsedSecs: number;
  runError: string | null;
  onRetry: () => void;
  focusedCitation: number | null;
  onCitationClick: (n: number, orphan: boolean) => void;
  onCitationFocus: (n: number | null) => void;
  // Enrichment state at run start — used by QueryProgress to include or
  // skip the "Enriching query" stage in its cycled label sequence.
  enrichEnabled: boolean;
}

function ResponsePanel(p: ResponsePanelProps) {
  const { t, tt } = p;
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>{t('labs.responseHeader')}</div>

      {/* Empty state */}
      {!p.run && !p.running && !p.runError && (
        <div className={`${styles.emptyState} ${styles.readyState}`}>
          <p className={styles.emptyStateTitle}>{t('labs.responseReadyTitle')}</p>
          <p className={styles.emptyStateBody}>{t('labs.responseReadyBody')}</p>
        </div>
      )}

      {/* Loading — cycled stage labels + live elapsed timer.
        * Replaces the previous skeleton shimmer with something that
        * communicates the actual pipeline (enrich / retrieve / generate)
        * so the reviewer sees the system is alive and understands what's
        * happening during the ~10-15s Bedrock wait. Stage transitions are
        * time-based estimates calibrated to typical Bedrock EU latency;
        * the elapsed counter is real. */}
      {p.running && (
        <QueryProgress
          t={t}
          tt={tt}
          elapsedSecs={p.runElapsedSecs}
          enrichEnabled={p.enrichEnabled}
        />
      )}

      {/* Error */}
      {p.runError && !p.running && (
        <div className={styles.callout + ' ' + styles.calloutError} role="alert">
          <span className={styles.calloutErrorLabel}>{t('labs.responseErrorTitle')}</span>
          {p.runError}
          <div style={{ marginTop: 8 }}>
            <Button kind="ghost" onClick={p.onRetry}>
              {t('labs.responseErrorRetry')}
            </Button>
          </div>
        </div>
      )}

      {/* Results */}
      {p.run && !p.running && !p.runError && (
        p.run.response_without_corpus ? (
          <div className={styles.responseCompareGrid}>
            <ResponseCard
              t={t}
              tt={tt}
              response={p.run.response_with_corpus}
              retrievedCount={p.run.retrieved_chunks.length}
              citedCount={p.run.retrieved_chunks.filter((c) => c.was_cited_by_response).length}
              latencyMs={p.run.latency_ms}
              sub={t('labs.responseWithCorpusHeader')}
              subHighlighted
              onCitationClick={p.onCitationClick}
              onCitationFocus={p.onCitationFocus}
              focusedCitation={p.focusedCitation}
              zeroRetrieval={p.run.retrieved_chunks.length === 0}
            />
            <ResponseCard
              t={t}
              tt={tt}
              response={p.run.response_without_corpus}
              retrievedCount={0}
              citedCount={0}
              latencyMs={p.run.latency_ms}
              sub={t('labs.responseSonnetAloneHeader')}
              subHighlighted={false}
              onCitationClick={() => {
                /* no interaction on the bare side */
              }}
              onCitationFocus={() => {
                /* no-op */
              }}
              focusedCitation={null}
              zeroRetrieval={false}
              barNoContext
            />
          </div>
        ) : (
          <ResponseCard
            t={t}
            tt={tt}
            response={p.run.response_with_corpus}
            retrievedCount={p.run.retrieved_chunks.length}
            citedCount={p.run.retrieved_chunks.filter((c) => c.was_cited_by_response).length}
            latencyMs={p.run.latency_ms}
            onCitationClick={p.onCitationClick}
            onCitationFocus={p.onCitationFocus}
            focusedCitation={p.focusedCitation}
            zeroRetrieval={p.run.retrieved_chunks.length === 0}
          />
        )
      )}
    </div>
  );
}

// ─── ScopeDeclineRetrievalStrip ────────────────────────────────────────
//
// Replaces the retrieval panel on the right when the coach engine flagged
// the response as a coaching-scope decline (scope_declined=true). The
// coach still fires retrieval — Atlas $vectorSearch runs on every query,
// including declines — but Sonnet ignored the results and delivered a
// "here's what I CAN help with instead" reply per the coaching contract.
// Showing 15 retrieval rows in that case would mislead the reviewer into
// thinking the response was grounded on them; showing this strip makes it
// obvious that retrieval fired but wasn't the mechanism the coach used
// to answer.
//
// Kept intentionally minimal: title, one-sentence explanation, and the
// count of what WOULD have been retrieved (useful signal — a decline
// with 15 high-similarity chunks means retrieval was working; the coach
// just chose not to use them). No "show anyway" affordance in V1 — add
// it only if a reviewer asks to inspect what retrieval would have
// surfaced in a decline scenario.

interface ScopeDeclineRetrievalStripProps {
  t: (k: string) => string;
  retrievedCount: number;
}

function ScopeDeclineRetrievalStrip({ t, retrievedCount }: ScopeDeclineRetrievalStripProps) {
  return (
    <div className={styles.card} role="status">
      <div className={styles.cardHeader}>
        <span>{t('labs.retrievalHeader')}</span>
        <span className={styles.cardHeaderSub}>{t('labs.scopeDeclineRetrievalTag')}</span>
      </div>
      <div className={styles.scopeDeclineBody}>
        <p className={styles.scopeDeclineTitle}>{t('labs.scopeDeclineRetrievalTitle')}</p>
        <p className={styles.scopeDeclineExplanation}>
          {t('labs.scopeDeclineRetrievalBody')}
        </p>
        <p className={styles.scopeDeclineMeta}>
          {t('labs.scopeDeclineRetrievalCountLabel')}: {retrievedCount}
        </p>
      </div>
    </div>
  );
}

// ─── QueryProgress ────────────────────────────────────────────────────
//
// Cycled stage labels + live elapsed timer, shown while a query is
// running. Replaces the previous shimmer skeleton with something that
// tells the reviewer what phase of the pipeline we're in, so a 10-15s
// Bedrock wait feels like progress instead of a stall.
//
// Stage transitions are TIME-BASED ESTIMATES (not backend-truth) —
// calibrated to typical Bedrock EU latency observed in staging:
//   - Enrichment (Haiku, eu.anthropic.claude-haiku-4-5):        ~1.2-1.8s
//   - Embedding (Titan v2) + Atlas $vectorSearch combined:      ~0.3-0.6s
//   - Sonnet 4.6 response + citation cross-check on the coach:  ~8-12s
// The elapsed counter is REAL (driven by parent's Date.now() interval).
// Deliberately no percentage bar / ETA — LLM latency is high-variance
// and a fake bar reads as a broken promise the moment Bedrock is slow.
//
// Not a stateful component — the parent already ticks runElapsedSecs
// via setInterval; this just derives the current stage index from that
// elapsed value + whether enrichment is on.

interface QueryProgressProps {
  t: (k: string) => string;
  tt: (k: string, values: Record<string, string | number>) => string;
  elapsedSecs: number;
  enrichEnabled: boolean;
}

function QueryProgress({ t, tt, elapsedSecs, enrichEnabled }: QueryProgressProps) {
  // Stage list rebuilds based on enrichEnabled — skipping enrichment means
  // fewer visible steps in the dot pipeline.
  const stages: Array<{ labelKey: string; startsAtSec: number }> = enrichEnabled
    ? [
        { labelKey: 'labs.progressStageEnriching', startsAtSec: 0 },
        { labelKey: 'labs.progressStageRetrieving', startsAtSec: 2 },
        { labelKey: 'labs.progressStageGenerating', startsAtSec: 3 },
      ]
    : [
        { labelKey: 'labs.progressStageRetrieving', startsAtSec: 0 },
        { labelKey: 'labs.progressStageGenerating', startsAtSec: 1 },
      ];

  // Find the last stage whose startsAtSec <= elapsedSecs. The final stage
  // ("Generating response") stays sticky past its start time — LLM calls
  // can run long, and we'd rather the label sit still than lie about the
  // system being done.
  let stageIdx = 0;
  for (let i = 0; i < stages.length; i++) {
    if (elapsedSecs >= stages[i]!.startsAtSec) stageIdx = i;
  }
  const currentStage = stages[stageIdx]!;

  return (
    <div
      className={styles.queryProgress}
      aria-busy="true"
      aria-live="polite"
      role="status"
    >
      <div className={styles.queryProgressRow}>
        <Spinner />
        <div className={styles.queryProgressBody}>
          <div className={styles.queryProgressLabel}>{t(currentStage.labelKey)}</div>
          <div className={styles.queryProgressElapsed}>
            {tt('labs.progressElapsedTemplate', { s: elapsedSecs })}
          </div>
        </div>
      </div>
      {/* Mini stage-pipeline: 3 (or 2) dots, current one highlighted.
        * Gives the reviewer a mental model of the pipeline stages so
        * "Generating response" at 12s reads as "we're on the last step
        * and it's just slow" rather than "who knows what's happening." */}
      <ol className={styles.queryProgressPipeline}>
        {stages.map((s, i) => (
          <li
            key={s.labelKey}
            className={
              styles.queryProgressPipelineDot +
              ' ' +
              (i < stageIdx
                ? styles.queryProgressPipelineDotDone
                : i === stageIdx
                  ? styles.queryProgressPipelineDotActive
                  : styles.queryProgressPipelineDotPending)
            }
            aria-current={i === stageIdx ? 'step' : undefined}
          >
            <span className={styles.queryProgressPipelineDotLabel}>{t(s.labelKey)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

interface ResponseCardProps {
  t: (k: string) => string;
  tt: (k: string, values: Record<string, string | number>) => string;
  response: LabsCoachResponse;
  retrievedCount: number;
  citedCount: number;
  latencyMs: number;
  sub?: string;
  subHighlighted?: boolean;
  onCitationClick: (n: number, orphan: boolean) => void;
  onCitationFocus: (n: number | null) => void;
  focusedCitation: number | null;
  zeroRetrieval: boolean;
  barNoContext?: boolean;
}

function ResponseCard(p: ResponseCardProps) {
  const { t, tt } = p;
  const scopeDeclined = p.response.scope_declined === true;

  return (
    <div>
      {p.sub && (
        <div
          className={`${styles.responseColumnSub} ${p.subHighlighted ? styles.responseColumnSubWithCorpus : ''}`}
        >
          {p.sub}
        </div>
      )}

      {/* Guardrail callout — shown above the response text when the coach
        * engine flagged scope_declined=true. Distinct from the orphan
        * citation warning (that's a fabrication signal); this one signals
        * "the coach chose not to answer per the coaching contract" —
        * expected behaviour, not an error. Neutral-info styling (not
        * warn/error) so reviewers don't misread declines as failures. */}
      {scopeDeclined && (
        <div className={`${styles.callout} ${styles.scopeDeclineCallout}`} role="status">
          <span className={styles.scopeDeclineCalloutLabel}>
            {t('labs.scopeDeclineResponseCalloutLabel')}
          </span>
          {t('labs.scopeDeclineResponseCalloutBody')}
        </div>
      )}

      {/* Zero retrieval — informational, only when we actually TRIED to
        * use retrieval (i.e. not a scope-decline where retrieval is moot). */}
      {p.zeroRetrieval && !scopeDeclined && (
        <div className={`${styles.callout} ${styles.calloutWarn}`} role="status">
          <span className={styles.calloutWarnLabel}>{t('labs.zeroRetrievalCalloutLabel')}</span>
          {t('labs.zeroRetrievalCalloutBody')}
        </div>
      )}

      <div className={styles.responseContent}>
        <ResponseText
          markdown={p.response.markdown}
          validCitations={new Set(p.response.citations)}
          orphanCitations={new Set(p.response.orphan_citations)}
          onCitationClick={p.onCitationClick}
          onCitationFocus={p.onCitationFocus}
          focusedCitation={p.focusedCitation}
          citationTitle={t('labs.citationChipTitle')}
          orphanTitle={t('labs.citationChipOrphanTitle')}
        />
      </div>

      {/* Orphan callouts — one per orphan index */}
      {p.response.orphan_citations.map((n) => (
        <div key={n} className={`${styles.callout} ${styles.calloutWarn}`} role="status">
          <span className={styles.calloutWarnLabel}>{t('labs.orphanCalloutLabel')}</span>
          {tt('labs.orphanCalloutBodyTemplate', { n, m: p.retrievedCount })}
        </div>
      ))}

      <div className={styles.metadataFooter}>
        <span>{p.response.model_id}</span>
        <span>{tt('labs.metadataLatencyTemplate', { s: (p.latencyMs / 1000).toFixed(2) })}</span>
        {p.barNoContext ? (
          <span>{t('labs.metadataNoContext')}</span>
        ) : (
          <>
            <span>{tt('labs.metadataRetrievedTemplate', { n: p.retrievedCount })}</span>
            <span>{tt('labs.metadataCitedTemplate', { m: p.citedCount, n: p.retrievedCount })}</span>
          </>
        )}
      </div>
    </div>
  );
}

// Minimal markdown renderer for Labs response text — Sonnet reaches for
// `## headings`, `**bold**`, `*italic*`, `- lists`, `1. lists`, and `---`
// horizontal rules, so the raw pre-wrap approach left the response reading
// as literal markdown syntax (## and ** noise everywhere).
//
// Block parsing: split on blank lines, dispatch by leading token. Inline
// parsing: single regex over **bold** | *italic* | [N] citation tokens.
// The `[N]` chip pass has to interleave with bold/italic because Sonnet
// puts citations mid-sentence inside emphasized text ("... **contract
// policy** [6]"). Keeping chip interactivity (onClick/onFocus/onMouseEnter
// for retrieval-panel focus sync) is why this hand-rolled renderer beats
// pulling in react-markdown — we'd need to walk the AST anyway to swap
// bracket tokens for focusable buttons, and the surface Sonnet uses is
// small enough to hand-roll cleanly.
//
// Pattern intentionally matches consumer's CoachMarkdown (packages/
// consumer/src/routes/Coach.tsx) — same block-then-inline approach,
// extended here for the extra token types Sonnet emits in QA-bench mode.

interface InlineRenderContext {
  validCitations: Set<number>;
  orphanCitations: Set<number>;
  onCitationClick: (n: number, orphan: boolean) => void;
  onCitationFocus: (n: number | null) => void;
  focusedCitation: number | null;
  citationTitle: string;
  orphanTitle: string;
}

function ResponseText({
  markdown,
  validCitations,
  orphanCitations,
  onCitationClick,
  onCitationFocus,
  focusedCitation,
  citationTitle,
  orphanTitle,
}: {
  markdown: string;
  validCitations: Set<number>;
  orphanCitations: Set<number>;
  onCitationClick: (n: number, orphan: boolean) => void;
  onCitationFocus: (n: number | null) => void;
  focusedCitation: number | null;
  citationTitle: string;
  orphanTitle: string;
}) {
  const ctx: InlineRenderContext = {
    validCitations,
    orphanCitations,
    onCitationClick,
    onCitationFocus,
    focusedCitation,
    citationTitle,
    orphanTitle,
  };
  // Sonnet frequently emits a GFM pipe table immediately after a
  // paragraph header with NO blank line between them (e.g. "Four
  // domains:\n| Domain | Metrics |\n|---|---|"). The block-splitter
  // below groups them into one block, so table detection in renderBlock
  // (which only checks lines[0]/lines[1]) misses it and the whole thing
  // renders as literal pipe characters in a <p>. Preprocessing here
  // guarantees each table is its own block by inserting blank lines
  // around every table it finds.
  const blocks = normalizeTableBoundaries(markdown).split(/\n{2,}/);
  return (
    <>
      {blocks.map((block, i) => renderBlock(block, i, ctx))}
    </>
  );
}

// Ensure every GFM pipe table sits in its own \n\n-delimited block.
// Two passes:
//   1. Insert a blank line BEFORE a table start (a line containing `|`
//      followed by a separator line of only `|`, `-`, `:`, spaces) that
//      isn't already preceded by a blank line.
//   2. Insert a blank line AFTER a table row (any line matching `|...|`)
//      that isn't followed by another `|` or a blank line.
// Idempotent: running it twice on already-normalized text is a no-op.
function normalizeTableBoundaries(md: string): string {
  const withPreBlank = md.replace(
    /([^\n])\n(\|[^\n]*\|\n[ \t]*\|[ \t|:-]+\|)/g,
    '$1\n\n$2',
  );
  const withPostBlank = withPreBlank.replace(
    /(\|[^\n]*\|)\n(?!\||\n|$)/g,
    '$1\n\n',
  );
  return withPostBlank;
}

function renderBlock(block: string, key: number, ctx: InlineRenderContext): React.ReactNode {
  const trimmed = block.trim();
  if (!trimmed) return null;

  // Horizontal rule — three or more dashes on their own
  if (/^-{3,}$/.test(trimmed)) {
    return <hr key={key} className={styles.mdHr} />;
  }

  // Headings — ###, ##, # (Sonnet usually emits ##)
  if (trimmed.startsWith('### ')) {
    return (
      <h4 key={key} className={styles.mdH4}>
        {renderInline(trimmed.slice(4), ctx, `${key}-h4`)}
      </h4>
    );
  }
  if (trimmed.startsWith('## ')) {
    return (
      <h3 key={key} className={styles.mdH3}>
        {renderInline(trimmed.slice(3), ctx, `${key}-h3`)}
      </h3>
    );
  }
  if (trimmed.startsWith('# ')) {
    return (
      <h2 key={key} className={styles.mdH2}>
        {renderInline(trimmed.slice(2), ctx, `${key}-h2`)}
      </h2>
    );
  }

  const lines = trimmed.split(/\n/);

  // Blockquote — every line starts with `> ` (or bare `>`). Sonnet uses
  // this for callouts / rules of thumb / warnings. We strip the marker,
  // pass the remainder through inline formatting, and wrap the whole
  // block in a <blockquote> with a subtle left border + slate background.
  if (lines.every((l) => /^>\s?/.test(l))) {
    return (
      <blockquote key={key} className={styles.mdBlockquote}>
        {lines.map((line, j) => (
          <p key={j} className={styles.mdBlockquoteP}>
            {renderInline(line.replace(/^>\s?/, ''), ctx, `${key}-bq-${j}`)}
          </p>
        ))}
      </blockquote>
    );
  }

  // Table — GFM pipe format. First line is header, second line is
  // separator (`|---|---|` or `| :---: |`), remaining lines are body
  // rows. Sonnet reaches for tables when comparing categories side-by-
  // side (e.g., a MECE metrics matrix). Detect: at least 2 lines, first
  // includes `|`, second is entirely `|`, `-`, `:`, whitespace. Wrap in
  // a scroll container so wide tables never break the response column.
  if (
    lines.length >= 2 &&
    lines[0]!.includes('|') &&
    /^[\s|:-]+$/.test(lines[1]!) &&
    lines[1]!.includes('-')
  ) {
    const headers = parseTableRow(lines[0]!);
    const body = lines.slice(2).map(parseTableRow);
    return (
      <div key={key} className={styles.mdTableWrap}>
        <table className={styles.mdTable}>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} className={styles.mdTh}>
                  {renderInline(h, ctx, `${key}-th-${i}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c} className={styles.mdTd}>
                    {renderInline(cell, ctx, `${key}-td-${r}-${c}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Unordered list — every non-empty line starts with `- ` or `* `
  if (lines.every((l) => /^[-*]\s+/.test(l))) {
    return (
      <ul key={key} className={styles.mdUl}>
        {lines.map((line, j) => (
          <li key={j} className={styles.mdLi}>
            {renderInline(line.replace(/^[-*]\s+/, ''), ctx, `${key}-ul-${j}`)}
          </li>
        ))}
      </ul>
    );
  }

  // Ordered list — every non-empty line starts with `N. `
  if (lines.every((l) => /^\d+\.\s+/.test(l))) {
    return (
      <ol key={key} className={styles.mdOl}>
        {lines.map((line, j) => (
          <li key={j} className={styles.mdLi}>
            {renderInline(line.replace(/^\d+\.\s+/, ''), ctx, `${key}-ol-${j}`)}
          </li>
        ))}
      </ol>
    );
  }

  // Default: paragraph. Single-newline breaks within a paragraph are
  // preserved via CSS `white-space: pre-wrap` on .mdP.
  return (
    <p key={key} className={styles.mdP}>
      {renderInline(trimmed, ctx, `${key}-p`)}
    </p>
  );
}

// Parse one GFM pipe-table row into cells. `| a | b |` → ['a', 'b'].
// Trailing/leading pipes produce empty strings after split — drop them.
// Cell contents keep any inline markdown; the caller runs renderInline on
// each so **bold** / *italic* / [N] chips work inside table cells too.
function parseTableRow(line: string): string[] {
  const raw = line.split('|');
  if (raw.length > 0 && raw[0]!.trim() === '') raw.shift();
  if (raw.length > 0 && raw[raw.length - 1]!.trim() === '') raw.pop();
  return raw.map((c) => c.trim());
}

// Inline tokenizer — walks left-to-right through the text, emitting a mix
// of plain-text fragments, <strong> (bold), <em> (italic), and citation
// chip buttons. The single regex tries alternatives in order: **bold**
// wins over *italic* at the same position (bold is a superset pattern).
//
// Citation lookahead `(?![:([])` skips markdown link/reference syntax
// like `[1]:` (footnote definition), `[1](url)`, or `[1][ref]`.
function renderInline(
  text: string,
  ctx: InlineRenderContext,
  keyPrefix: string,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const pattern = /\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|\[(\d{1,3})\](?![:([])/g;
  let cursor = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > cursor) {
      parts.push(
        <Fragment key={`${keyPrefix}-t-${key++}`}>
          {text.slice(cursor, m.index)}
        </Fragment>,
      );
    }
    if (m[1] !== undefined) {
      parts.push(<strong key={`${keyPrefix}-b-${key++}`}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      parts.push(<em key={`${keyPrefix}-i-${key++}`}>{m[2]}</em>);
    } else if (m[3] !== undefined) {
      const n = parseInt(m[3], 10);
      const orphan = ctx.orphanCitations.has(n);
      const active = ctx.focusedCitation === n;
      parts.push(
        <button
          key={`${keyPrefix}-c-${key++}`}
          type="button"
          className={
            styles.citationChip +
            (orphan ? ' ' + styles.citationChipOrphan : '') +
            (active ? ' ' + styles.citationChipActive : '')
          }
          title={orphan ? ctx.orphanTitle : ctx.citationTitle}
          onClick={() => ctx.onCitationClick(n, orphan)}
          onFocus={() => ctx.onCitationFocus(n)}
          onMouseEnter={() => ctx.onCitationFocus(n)}
          onMouseLeave={() => ctx.onCitationFocus(null)}
        >
          {orphan ? `⚠ [${n}]` : `[${n}]`}
        </button>,
      );
      // Suppress unused-variable warning — validCitations is part of the
      // ctx shape for future use (per-index styling variants) but the
      // orphan check is what drives current rendering.
      void ctx.validCitations;
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) {
    parts.push(
      <Fragment key={`${keyPrefix}-t-${key++}`}>
        {text.slice(cursor)}
      </Fragment>,
    );
  }
  return parts;
}

// ─── RetrievalPanel (right column) ─────────────────────────────────────

interface RetrievalPanelProps {
  t: (k: string) => string;
  tt: (k: string, values: Record<string, string | number>) => string;
  chunks: LabsRetrievedChunk[];
  totalCited: number;
  running: boolean;
  expandedRowIdx: number | null;
  onRowClick: (i: number) => void;
  onRowFocus: (i: number) => void;
  focusedCitation: number | null;
  rowRefs: React.MutableRefObject<Array<HTMLDivElement | null>>;
  hasRunOnce: boolean;
  onClearFilters: () => void;
  onIncludeDrafts: () => void;
}

function RetrievalPanel(p: RetrievalPanelProps) {
  const { t, tt } = p;
  const n = p.chunks.length;
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span>{tt('labs.retrievedHeaderTemplate', { n })}</span>
        {n > 0 && (
          <span className={styles.retrievalHeaderChip}>
            {tt('labs.citedChipTemplate', { m: p.totalCited, n })}
          </span>
        )}
      </div>

      {/* Loading */}
      {p.running && (
        <div aria-busy="true" aria-live="polite">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className={styles.skeletonLine} style={{ height: 40, margin: '10px 0' }} />
          ))}
        </div>
      )}

      {/* Empty (no run yet) */}
      {!p.running && !p.hasRunOnce && (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateBody}>{t('labs.retrievalEmpty')}</p>
        </div>
      )}

      {/* Zero results (ran but nothing came back) */}
      {!p.running && p.hasRunOnce && n === 0 && (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateTitle}>{t('labs.retrievalZeroTitle')}</p>
          <p className={styles.emptyStateBody}>{t('labs.retrievalZeroBody')}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
            <Button kind="ghost" onClick={p.onClearFilters}>
              {t('labs.retrievalZeroClearFilters')}
            </Button>
            <Button kind="ghost" onClick={p.onIncludeDrafts}>
              {t('labs.retrievalZeroIncludeDrafts')}
            </Button>
          </div>
        </div>
      )}

      {/* Rows */}
      {!p.running && n > 0 && (
        <div className={styles.retrievalRows}>
          {p.chunks.map((chunk, i) => (
            <RetrievalRow
              key={chunk.kb_entry_id + '-' + i}
              t={t}
              tt={tt}
              chunk={chunk}
              index={i}
              highlighted={p.focusedCitation === i + 1}
              expanded={p.expandedRowIdx === i}
              onClick={() => p.onRowClick(i)}
              onFocus={() => p.onRowFocus(i)}
              rowRef={(el) => {
                p.rowRefs.current[i] = el;
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface RetrievalRowProps {
  t: (k: string) => string;
  tt: (k: string, values: Record<string, string | number>) => string;
  chunk: LabsRetrievedChunk;
  index: number;
  highlighted: boolean;
  expanded: boolean;
  onClick: () => void;
  onFocus: () => void;
  rowRef: (el: HTMLDivElement | null) => void;
}

function RetrievalRow(p: RetrievalRowProps) {
  const { t, tt, chunk } = p;
  const n = p.index + 1;
  return (
    <div>
      <div
        ref={p.rowRef}
        tabIndex={0}
        role="button"
        aria-expanded={p.expanded ? 'true' : 'false'}
        aria-label={`Retrieved chunk ${n} — ${chunk.title}. Score ${chunk.similarity_score.toFixed(2)}. ${chunk.was_cited_by_response ? t('labs.citedIndicatorTitle') : t('labs.notCitedIndicatorTitle')}.`}
        className={
          styles.retrievalRow + (p.highlighted ? ' ' + styles.retrievalRowHighlighted : '')
        }
        onClick={p.onClick}
        onFocus={p.onFocus}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            p.onClick();
          }
        }}
      >
        <div className={styles.retrievalRowLine1}>
          <span className={styles.retrievalRowLeftCluster}>
            <CitationDot
              cited={chunk.was_cited_by_response}
              titleCited={t('labs.citedIndicatorTitle')}
              titleNotCited={t('labs.notCitedIndicatorTitle')}
            />
            <TypeBadge t={t} type={chunk.type} />
          </span>
          <span className={styles.retrievalRowScore}>{chunk.similarity_score.toFixed(2)}</span>
        </div>
        <div className={styles.retrievalRowTitle}>{chunk.title}</div>
        <div className={styles.retrievalRowLine3}>
          <span className={styles.retrievalRowSourceRef} title={chunk.source_ref}>
            {chunk.source_ref}
          </span>
          <span className={styles.retrievalRowMeta}>
            {chunk.domain} · {chunk.language.toUpperCase()}
          </span>
        </div>
      </div>
      {p.expanded && (
        <div className={styles.retrievalRowExpanded}>
          <div className={styles.retrievalRowExpandedSnippet}>{chunk.snippet}</div>
          <div className={styles.retrievalRowExpandedMeta}>
            <span className={styles.retrievalRowExpandedChip}>{chunk.domain}</span>
            <span className={styles.retrievalRowExpandedChip}>{chunk.language.toUpperCase()}</span>
            <span className={styles.retrievalRowExpandedChip}>
              {chunk.was_cited_by_response
                ? tt('labs.expandCitedAsTemplate', { n })
                : t('labs.expandNotCited')}
            </span>
            <span className={styles.retrievalRowExpandedChip + ' ' + styles.retrievalRowChunkId}>
              {chunk.kb_entry_id}
            </span>
          </div>
          <a
            className={styles.retrievalRowOpenKb}
            href={`/admin/knowledge/base/${encodeURIComponent(chunk.kb_entry_id)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('labs.openInKb')}
          </a>
        </div>
      )}
    </div>
  );
}

function CitationDot({
  cited,
  titleCited,
  titleNotCited,
}: {
  cited: boolean;
  titleCited: string;
  titleNotCited: string;
}) {
  return (
    <span
      className={cited ? styles.citationDotFilled : styles.citationDotHollow}
      title={cited ? titleCited : titleNotCited}
      aria-hidden="true"
    />
  );
}

// ─── TypeBadge ─────────────────────────────────────────────────────────

function TypeBadge({ t, type }: { t: (k: string) => string; type: KBEntryType }) {
  const cls =
    type === 'qa_pair'
      ? styles.typeBadgeQa
      : type === 'framework'
        ? styles.typeBadgeFramework
        : type === 'case_study'
          ? styles.typeBadgeCase
          : styles.typeBadgeExcerpt;
  const labelKey =
    type === 'qa_pair'
      ? 'labs.badgeQaPair'
      : type === 'framework'
        ? 'labs.badgeFramework'
        : type === 'case_study'
          ? 'labs.badgeCaseStudy'
          : 'labs.badgeDocumentExcerpt';
  return (
    <span className={`${styles.typeBadge} ${cls}`}>
      <span className={styles.typeBadgeDot} aria-hidden="true" />
      {t(labelKey)}
    </span>
  );
}

// ─── Recent tests (history) ────────────────────────────────────────────

interface RecentTestsProps {
  t: (k: string) => string;
  tt: (k: string, values: Record<string, string | number>) => string;
  runs: LabsTestRunSummary[];
  loading: boolean;
  pendingDelete: string | null;
  onLoad: (id: string) => void;
  onDeleteRequest: (id: string) => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}

function RecentTests(p: RecentTestsProps) {
  const { t, tt } = p;
  return (
    <div className={styles.historyCard}>
      <div className={styles.cardHeader}>
        <span>{t('labs.historyHeader')}</span>
        <span className={styles.cardHeaderSub}>
          {tt('labs.historyHeaderSubTemplate', { n: 40 })}
        </span>
      </div>

      {p.runs.length === 0 && !p.loading && (
        <p className={styles.historyEmpty}>{t('labs.historyEmpty')}</p>
      )}

      {p.runs.length > 0 && (
        <table className={styles.historyTable}>
          <thead>
            <tr>
              <th style={{ width: '40%' }}>{t('labs.colQuery')}</th>
              <th>{t('labs.colPersona')}</th>
              <th>{t('labs.colDomain')}</th>
              <th>{t('labs.colRetrieved')}</th>
              <th>{t('labs.colCited')}</th>
              <th>{t('labs.colBy')}</th>
              <th>{t('labs.colWhen')}</th>
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {p.runs.map((row) => (
              <tr key={row.id}>
                <td className={styles.historyRowQuery} title={row.raw_query}>
                  {row.scope_declined && (
                    <span
                      className={styles.historyDeclinedTag}
                      title={t('labs.scopeDeclineHistoryTagTitle')}
                    >
                      {t('labs.scopeDeclineHistoryTag')}
                    </span>
                  )}
                  {row.raw_query}
                </td>
                <td>{row.persona_id ? shortId(row.persona_id) : '—'}</td>
                <td>{row.domain_filter ?? '—'}</td>
                <td>{row.retrieved_count}</td>
                <td>
                  {row.cited_count}/{row.retrieved_count}
                </td>
                <td className={styles.historyRowBy}>{initials(row.created_by_label ?? row.created_by)}</td>
                <td className={styles.historyRowWhen}>{formatRelative(row.created_at)}</td>
                <td>
                  <button
                    type="button"
                    className={styles.historyLoadButton}
                    onClick={() => p.onLoad(row.id)}
                  >
                    {t('labs.loadButton')}
                  </button>
                  <button
                    type="button"
                    className={styles.historyRowOverflow}
                    onClick={() => p.onDeleteRequest(row.id)}
                    title={t('labs.deleteRowLabel')}
                    aria-label={t('labs.deleteRowLabel')}
                  >
                    ⋯
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {p.pendingDelete && (
        <div className={styles.confirmInline}>
          <div className={styles.confirmInlineTitle}>{t('labs.deleteConfirmTitle')}</div>
          <div className={styles.confirmInlineBody}>{t('labs.deleteConfirmBody')}</div>
          <div className={styles.confirmInlineButtons}>
            <Button kind="ghost" onClick={p.onDeleteCancel}>
              {t('labs.deleteConfirmCancel')}
            </Button>
            <Button kind="danger" onClick={p.onDeleteConfirm}>
              {t('labs.deleteConfirmAction')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Small helpers ─────────────────────────────────────────────────────

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 6) + '…' : id;
}

function initials(label: string): string {
  const bits = label.trim().split(/\s+/);
  if (bits.length >= 2) return (bits[0]![0]! + bits[1]![0]!).toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.round((now - then) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

